import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { FormBuilder, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import flatpickr from 'flatpickr';
import moment from 'moment';
import { take, skip } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { RouteEventsService } from 'src/app/route-events.service';
import { CountryDateFormats, LocalizationService } from 'src/app/services/localization.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { PackagesService } from 'src/app/services/packages.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { PhoneNumberService } from 'src/app/services/phone-number.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { ScheduleConflictsComponent } from '../../utility/schedule-conflicts/schedule-conflicts.component';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RecurrenceConfig } from '../standing-series/standing-series.component';
import { StandingSeriesSaveService, StandingSeriesSaveResult } from '../../../services/standing-series-save.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-event-lesson-entity',
  templateUrl: './event-lesson-entity.component.html',
  styleUrls: ['./event-lesson-entity.component.css'],
  animations: [
    trigger('slideFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0)' }),
        animate('120ms ease-in', style({ opacity: 0, transform: 'translateY(-6px)' }))
      ])
    ])
  ]
})

export class EventLessonEntityComponent extends BaseDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  override eckType = "events";
  override bundle = "lesson";
  override entityId = this._injectedDialogData['data']['EntityID'];

  entityType = "attendees";
  entityBundle = "attendance";

  field_instructor: any = null;

  conflictingEvent: any = null;
  alternativeTimes: any[] = [];
  private lessonTypeSetFromPackage: boolean = false;
  lessonTypeManuallySelected: boolean = false;

  // Opened from a calendar "Coaching" menu: lesson type is preset to Non Unit
  // Private and the enrollment list only shows enrollments of this package.
  // The student's oldest regular enrollment must not reset the lesson type.
  coachingPackage: { id: string, name: string } | null = this._injectedDialogData?.['data']?.['fieldsData']?.['__coaching_package'] ?? null;
  coachingNoEnrollment: boolean = false;

  // Validation state for autocomplete fields
  studentAccountFormatError: boolean = false;
  flatpickrDisplayValue = '';
  flatpickrDateFormat = 'Y-m-d H:i';
  private flatpickrInstance?: flatpickr.Instance;
  private studioConfigCountry: string = 'US';
  private isUpdatingFromFlatpickr = false;
  useMobileSplitPicker = false;
  // Native HTML5 picker values (mobile). type="date" expects YYYY-MM-DD and
  // type="time" expects HH:mm. We keep them as strings and combine on change.
  mobileDateValue = '';
  mobileTimeValue = '';
  specificExpirationDateValue: string = ''; // Separate value for the actual date
  valueSpecificDate: string = 'specific'; // Constant value for "specific date" radio option

  // Recurring series state. The create/generate choreography lives in
  // StandingSeriesSaveService (shared with the services dialog); this component
  // only holds UI state and reacts to the service's result.
  //
  // Built in ngOnInit from the base class's already-injected REST services,
  // NOT via inject()/constructor DI (Angular 13 NG0203 in field initializers
  // on the manual ComponentFactory create path) and NOT a template-reachable
  // lazy getter (would mutate component state during change detection →
  // NG0100). ngOnInit runs before the first template evaluation.
  private _seriesSave!: StandingSeriesSaveService;
  isRecurring = false;
  recurrenceConfig: RecurrenceConfig | null = null;
  seriesConflicts: any[] = [];
  pendingSeriesId: number | null = null;
  selectedEnrollmentForSeries: any = null;

  // Track mobile landscape state
  private _isLandscapeMode: boolean = false;

  // Outside-studio-hours warning state. Populated by the server check at
  // /api_entity/events/lesson/outside-hours-check. When outside_hours is true
  // we render a stern banner. The user can still save, but they've been told.
  outsideHoursWarning: {
    outside_hours: boolean;
    reason: string;
    timezone: string | null;
    local_start: string | null;
    local_end: string | null;
    weekday: number | null;
    slots: { start: string; end: string }[];
  } | null = null;
  private _outsideHoursCheckTimer: any = null;
  private _outsideHoursCheckSeq = 0;

  constructor(
    _dialogRef: MatDialogRef<unknown>,
    _dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) _injectedDialogData: any,
    _refreshService: RefreshService,
    _dialogService: DialogService,
    _entityRESTService: EntityRESTService,
    _utilityService: UtilityService,
    _fieldsService: FieldsService,
    _drupalRESTService: DrupalRESTService,
    _taxonomyService: TaxonomyService,
    _router: Router,
    _activatedRoute: ActivatedRoute,
    _authRESTService: AuthRESTService,
    _authService: AuthService,
    _menuRESTService: MenuRESTService,
    _formBuilder: FormBuilder,
    _routeEventsService: RouteEventsService,
    _permissionsService: PermissionsService,
    _renderer2: Renderer2,
    _configPages: ConfigPagesService,
    _packagesService: PackagesService,
    _snackBar: MatSnackBar,
    _userRESTService: UserService,
    _el: ElementRef,
    _changeDetectorRef: ChangeDetectorRef,
    _sanitizer: DomSanitizer,
    _studioConfigService: StudioConfigService,
    _componentRefreshService: ComponentRefreshService,
    _translateService: TranslateService,
    private readonly localizationService: LocalizationService,
    phoneNumberService: PhoneNumberService,
    private breakpointObserver: BreakpointObserver
  ) {
    super(
      _dialogRef,
      _dialog,
      _injectedDialogData,
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService,
      phoneNumberService
    );

    this.useMobileSplitPicker = this.isMobileViewport();

    // Initialize landscape mode state immediately to avoid change detection errors
    this._isLandscapeMode = this.checkIsLandscapeMode();

    _studioConfigService.getCountryCode().pipe(take(1)).subscribe((country) => {
      if (country) {
        this.studioConfigCountry = country;
        if (!this.useMobileSplitPicker && this.flatpickrInstance) {
          const formats = this.localizationService.getDateFormatsForCountry(country);
          const dateFormat = `${formats.flatpickrDateFormat} h:i K`;
          this.flatpickrDateFormat = dateFormat;
          this.flatpickrInstance.set('dateFormat', dateFormat);
          this.flatpickrInstance.set('minuteIncrement', 15);
        }
        if (!this.useMobileSplitPicker) {
          this.openFlatpickr();
        }
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Build the shared save service from the already-injected REST services.
    // See the field comment for why this is here, not a field initializer or
    // a template-reachable getter (Angular 13 NG0203 / NG0100).
    this._seriesSave = new StandingSeriesSaveService(
      this._entityRESTService,
      this._drupalRESTService,
    );

    this.initializeFlatpickrDisplayValue(this.field_date_and_time);

    // Handle existing field_expiration_date when editing
    if (this.field_expiration_date &&
        this.field_expiration_date !== this.valueEOM &&
        this.field_expiration_date !== this.valueEOY &&
        this.field_expiration_date !== this.valueSpecificDate) {
      // It's an actual date value, move it to specificExpirationDateValue
      this.specificExpirationDateValue = this.field_expiration_date;
      this.field_expiration_date = this.valueSpecificDate;
    }

    this._studioConfigService.getCountryCode().pipe(take(1)).subscribe((country) => {
      if (country) {
        this.studioConfigCountry = country;
        if (this.flatpickrInstance) {
          const formats = this.localizationService.getDateFormatsForCountry(country);
          const dateFormat = `${formats.flatpickrDateFormat} h:i K`;
          this.flatpickrDateFormat = dateFormat;
          this.flatpickrInstance.set('dateFormat', dateFormat);
          this.flatpickrInstance.set('minuteIncrement', 15);
        }
        this.openFlatpickr();
      }
    });

    // If we're editing an existing lesson, evaluate the hours warning once
    // after the form values have been patched in.
    setTimeout(() => this.checkOutsideHours(), 400);
  }

  @ViewChild('scheduleConflicts') scheduleConflicts: ScheduleConflictsComponent;
  @ViewChild('flatpickrInput', { static: false }) flatpickrInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileDateInput', { static: false }) mobileDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileTimeInput', { static: false }) mobileTimeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('landscapeCalendarInput', { static: false }) landscapeCalendarInput?: ElementRef<HTMLInputElement>;

  // Landscape mode time picker properties
  landscapeHour: number = 12;
  landscapeMinute: string = '00';
  landscapePeriod: string = 'PM';
  private landscapeCalendarPicker?: flatpickr.Instance;

  // Combined hour + period options (12 AM, 1 AM, ..., 11 PM, 12 PM)
  hourPeriodOptions = [
    { hour: 12, period: 'AM', display: '12 AM' },
    { hour: 1, period: 'AM', display: '1 AM' },
    { hour: 2, period: 'AM', display: '2 AM' },
    { hour: 3, period: 'AM', display: '3 AM' },
    { hour: 4, period: 'AM', display: '4 AM' },
    { hour: 5, period: 'AM', display: '5 AM' },
    { hour: 6, period: 'AM', display: '6 AM' },
    { hour: 7, period: 'AM', display: '7 AM' },
    { hour: 8, period: 'AM', display: '8 AM' },
    { hour: 9, period: 'AM', display: '9 AM' },
    { hour: 10, period: 'AM', display: '10 AM' },
    { hour: 11, period: 'AM', display: '11 AM' },
    { hour: 12, period: 'PM', display: '12 PM' },
    { hour: 1, period: 'PM', display: '1 PM' },
    { hour: 2, period: 'PM', display: '2 PM' },
    { hour: 3, period: 'PM', display: '3 PM' },
    { hour: 4, period: 'PM', display: '4 PM' },
    { hour: 5, period: 'PM', display: '5 PM' },
    { hour: 6, period: 'PM', display: '6 PM' },
    { hour: 7, period: 'PM', display: '7 PM' },
    { hour: 8, period: 'PM', display: '8 PM' },
    { hour: 9, period: 'PM', display: '9 PM' },
    { hour: 10, period: 'PM', display: '10 PM' },
    { hour: 11, period: 'PM', display: '11 PM' }
  ];

  onLessonTypeManualChange() {
    LoggerService.log('Lesson type manually changed by user');
    this.lessonTypeManuallySelected = true;
  }

  refreshEnrollmentsForLessonType(lessonType: string, studentAccount: string) {
    // Only refresh if we have a student selected
    if (!studentAccount || studentAccount.trim() === '') {
      return;
    }

    LoggerService.log('Refreshing enrollments for lesson type:', lessonType, 'student:', studentAccount);

    // Re-run the enrollment loading with the new lesson type
    this.onStudentNameSelectThenLoadEnrollmentNameLESSONS(
      { option: { value: studentAccount } },
      false,
      this?._frmValues?.field_student?.[0]?.field_enrollment,
      false,
      'createLesson',
      lessonType
    );
  }

  studentIncrement(attendee: any) {
    LoggerService.log('attendee');
    LoggerService.log(attendee);

    // Convert comma-separated field_students back to array format for saving
    if (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(',')) {
      attendee.field_students = attendee.field_students.split(',');
    }

    // Append type & bundle to model.
    attendee = {
      ...attendee,
      type: 'attendees',
      bundle: 'attendance',
    }

    // If our data binding fails us (API needs to give an array), set as an empty array.
    if (!Array.isArray(this.field_student)) { this.field_student = [] }

    // Capture the selected enrollment object before reset clears enrollmentsForUser
    if (attendee.field_enrollment) {
      this.selectedEnrollmentForSeries = this.getEnrollmentById(attendee.field_enrollment);
    }

    this.field_student.push(attendee);

    this.resetFieldStudent();
  }

  studentRemove = (index: number): void => {
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;
    this.field_student.splice(index, 1);
    this.selectedEnrollmentForSeries = null;

    this.resetFieldStudent();
  }

  studentUpdateLoad = (index: number): void => {
    this.resetFieldStudent();

    // Load the student enrollments again. Don't auto-select during edit mode.
    this.lookupStudentAccountContact({ option: { value: this.field_student[index].field_student_account } }, false);
    // Load the student contacts again.
    this.onStudentNameSelectThenLoadEnrollmentNameLESSONS({ option: { value: this.field_student[index].field_student_account } }, false, null, false, 'createLesson', this.f?.value?.field_type)

    // Append type & bundle to model.
    this.field_student[index]['type'] = 'attendees';
    this.field_student[index]['bundle'] = 'attendance';

    this.editIndex = index;
    this.editMode = true;

    // Wait for autoCompleteStudentContactOptions to be populated
    const waitForOptions = () => {
      if (this.autoCompleteStudentContactOptions && this.autoCompleteStudentContactOptions.length > 0) {
        // Keep the attendee id so the backend updates the existing attendees
        // entity in place instead of creating a new one on every edit.
        const formValue = { ...this.field_student[index] };

        // Convert array field_students back to comma-separated string for radio button matching
        if (Array.isArray(formValue.field_students)) {
          formValue.field_students = formValue.field_students.join(',');
        }

        this.f.controls['__field_students_inline_form'].patchValue(formValue);
      } else {
        // Options not ready yet, try again in 100ms
        setTimeout(waitForOptions, 100);
      }
    };

    waitForOptions();
  }

  // True when the inline attendee form holds a complete entry the user never
  // committed via the Add/Edit Attendee button.
  hasPendingAttendee(): boolean {
    const value = this.f?.form?.controls?.['__field_students_inline_form']?.value;
    return !!value?.field_student_account && !!value?.field_students && !this.studentAccountFormatError;
  }

  // Commit a pending inline attendee on Save so users who forget to click
  // Add/Edit Attendee don't silently lose their changes.
  commitPendingAttendee(): void {
    if (!this.hasPendingAttendee()) { return; }

    const value = { ...this.f.form.controls['__field_students_inline_form'].value };
    if (this.editMode) {
      this.studentUpdateSubmit(this.editIndex, value);
    } else {
      this.studentIncrement(value);
    }
  }

  studentUpdateSubmit(_number: any, attendee: any) {
    // Convert comma-separated field_students back to array format for saving
    if (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(',')) {
      attendee.field_students = attendee.field_students.split(',');
    }

    this.field_student[this.editIndex] = attendee;
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;

    this.resetFieldStudent();
  }

  resetFieldStudent() {
    this.enrollmentMessage = "";
    this.autoCompleteStudentContactOptions = null;
    this.enrollmentsForUser = null;
    this.f.form.controls['__field_students_inline_form'].reset();
    // Reset the lesson type flags when resetting the form
    this.lessonTypeSetFromPackage = false;
    this.lessonTypeManuallySelected = false;
    this.coachingNoEnrollment = false;
    // Reset validation errors
    this.studentAccountFormatError = false;
  }

  /**
   * Validate that a field value matches the expected autocomplete format "Name (ID)"
   */
  validateAutocompleteFormat(value: string): boolean {
    if (!value || value.trim() === '') {
      return true; // Empty is okay, required validation handles that
    }

    // Check if value is just a number (means it's just an ID)
    if (!isNaN(+value)) {
      return true; // Valid - just an ID
    }

    // Check if value contains (ID) pattern
    const regExp = /\(([^)]+)\)/g;
    const matches = value.match(regExp);

    return matches && matches.length > 0;
  }

  /**
   * Handle student account input change to validate format
   */
  onStudentAccountInput(value: string): void {
    this.studentAccountFormatError = !this.validateAutocompleteFormat(value);
  }

  displayConflicts(conflicts: any[]) {
    // Format the conflicts data for display
    const formattedConflicts = conflicts.map(conflict => {
      return `Event ID: ${conflict.id}, Date and Time: ${conflict.date_and_time}, Instructor: ${conflict.instructor}`;
    });

    // Show the conflicts in an alert or a modal dialog
    alert(`Schedule conflicts found:\n${formattedConflicts.join('\n')}`);
  }

  async checkScheduleConflicts() {
    if (this.scheduleConflicts) {
      await this.scheduleConflicts.checkScheduleConflicts();
    }
    this.checkOutsideHours();
  }

  /**
   * Asks the server whether the current date/time falls outside the studio's
   * working hours (interpreted in the studio's timezone). Populates
   * outsideHoursWarning. The template renders a stern banner when it's set.
   *
   * Debounced + sequence-guarded so a rapid-fire picker change doesn't leave
   * a stale response overwriting a fresh one.
   */
  checkOutsideHours(): void {
    if (this._outsideHoursCheckTimer) {
      clearTimeout(this._outsideHoursCheckTimer);
    }
    this._outsideHoursCheckTimer = setTimeout(() => this._runOutsideHoursCheck(), 250);
  }

  private _runOutsideHoursCheck(): void {
    const start = this.field_date_and_time;
    const studioId = this._authService.selectedStudio?.id;
    const duration = this.f?.value?.field_duration || '';

    if (!start || !studioId) {
      this.outsideHoursWarning = null;
      return;
    }

    const durationMinutes = this._parseDurationToMinutes(duration);
    const seq = ++this._outsideHoursCheckSeq;

    this._drupalRESTService.httpGET(
      '/api_entity/events/lesson/outside-hours-check',
      [
        { parameter: 'studio', value: String(studioId) },
        { parameter: 'start', value: String(start) },
        { parameter: 'duration_minutes', value: String(durationMinutes) },
      ]
    ).subscribe({
      next: (resp: any) => {
        if (seq !== this._outsideHoursCheckSeq) {
          return; // a newer check has been fired
        }
        this.outsideHoursWarning = resp && resp.outside_hours ? resp : null;
      },
      error: (err: any) => {
        if (seq !== this._outsideHoursCheckSeq) {
          return;
        }
        // Don't block the user if the check itself failed.
        LoggerService.warn('Outside-hours check failed', err);
        this.outsideHoursWarning = null;
      }
    });
  }

  /**
   * Parses a duration value (HH:MM or minutes) into an integer minute count.
   */
  private _parseDurationToMinutes(duration: any): number {
    if (duration == null || duration === '') {
      return 0;
    }
    const str = String(duration).trim();
    if (str.includes(':')) {
      const [h, m] = str.split(':');
      const hours = parseInt(h, 10) || 0;
      const minutes = parseInt(m, 10) || 0;
      return hours * 60 + minutes;
    }
    const n = parseInt(str, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /**
   * Human text for the banner headline based on the server-supplied reason.
   */
  outsideHoursHeadline(): string {
    const w = this.outsideHoursWarning;
    if (!w) return '';
    switch (w.reason) {
      case 'before_open':      return 'That time is before the studio opens.';
      case 'after_close':      return 'That time is after the studio closes.';
      case 'closed_day':       return 'The studio is closed that day.';
      case 'between_slots':    return 'That time falls during a closed block in the studio schedule.';
      case 'ends_after_close': return 'This lesson would run past the studio\'s closing time.';
      default:                 return 'That time is outside the studio\'s working hours.';
    }
  }

  /**
   * A compact "Hours on Monday: 10:00 AM – 8:00 PM" line for the banner.
   */
  outsideHoursHoursLine(): string {
    const w = this.outsideHoursWarning;
    if (!w || w.weekday == null) return '';
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[w.weekday] ?? '';
    if (!w.slots || w.slots.length === 0) {
      return `The studio is closed on ${dayName}.`;
    }
    const fmt = (hhmm: string) => {
      const [hStr, mStr] = hhmm.split(':');
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (h === 24 && m === 0) return '12:00 AM';
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = ((h % 12) === 0) ? 12 : (h % 12);
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    };
    const slotStrs = w.slots.map(s => `${fmt(s.start)} – ${fmt(s.end)}`);
    return `Hours on ${dayName}: ${slotStrs.join(', ')}.`;
  }

  dismissOutsideHoursWarning(): void {
    this.outsideHoursWarning = null;
  }

  /**
   * Formatted AM/PM nudge text using the user's actual entered time. Only
   * shown when flipping AM↔PM on the start time lands inside one of the day's
   * working-hour slots. Otherwise returns '' (caller falls back to generic
   * copy) so we don't suggest "did you mean 10:30 AM?" when 10:30 AM is also
   * closed.
   *
   * Uses the slots already in outsideHoursWarning; no extra server call.
   * Flipping only shifts the hour by 12, so the weekday (and therefore the
   * slots) are unchanged.
   */
  outsideHoursAmPmNudge(): string {
    const w = this.outsideHoursWarning;
    if (!w || !w.local_start || !w.slots) return '';
    const m = moment(w.local_start, ['YYYY-MM-DD HH:mm', moment.ISO_8601], true);
    if (!m.isValid()) return '';

    const hour = m.hour();
    const flipped = hour < 12 ? m.clone().add(12, 'hours') : m.clone().subtract(12, 'hours');
    const flippedMinutes = flipped.hour() * 60 + flipped.minute();

    const hhmmToMinutes = (hhmm: string): number => {
      const [h, mm] = hhmm.split(':').map(n => parseInt(n, 10) || 0);
      return h * 60 + mm;
    };
    const insideASlot = w.slots.some(s => {
      const start = hhmmToMinutes(s.start);
      const end = hhmmToMinutes(s.end);
      return flippedMinutes >= start && flippedMinutes < end;
    });
    if (!insideASlot) return '';

    return `did you mean ${flipped.format('h:mm A')} instead of ${m.format('h:mm A')}?`;
  }

  override updateLessonDefaultTimes(lessonTypeId: string, eventType?: any): string {
    // Call the parent method to handle default behavior
    const defaultDuration = super.updateLessonDefaultTimes(lessonTypeId, eventType);

    return defaultDuration;
  }

  override validateEnrollment(lessontype: any, studentAccount: string, enrollmentId: string) {
    // Let the parent handle the API call for validation
    super.validateEnrollment(lessontype, studentAccount, enrollmentId);

    if (!enrollmentId || enrollmentId === 'None') {
      return;
    }

    LoggerService.log('Fetching package data for enrollment:', enrollmentId);

    // Get the package data from the enrollment
    this._entityRESTService.getEntity('packages', 'enrollment', enrollmentId)
      .subscribe((enrollmentData: any) => {
        LoggerService.log('Enrollment data:', enrollmentData);

        // Check if enrollment has a package reference
        if (enrollmentData?.field_enrollment_package_name?.id) {
          const packageId = enrollmentData.field_enrollment_package_name.id;

          // Get the package data to check for field_lesson_type
          this._entityRESTService.getEntity('packages', 'package', packageId)
            .subscribe((packageData: any) => {
              LoggerService.log('Package data:', packageData);

              // For Non-Unit Private (1411) and Non-Unit Group (1410),
              // clear the enrollment if the package lesson type does not match the selected lesson type.
              const selectedLessonType = this.f?.value?.field_type;
              const packageLessonType =
                packageData?.field_lesson_type ||
                packageData?.field_lesson_type?.[0]?.target_id ||
                packageData?.field_lesson_type?.[0]?.value;

              const isNonUnitContext = selectedLessonType === '1410' || selectedLessonType === '1411';
              const lessonTypeMismatch =
                isNonUnitContext &&
                packageLessonType &&
                selectedLessonType &&
                String(packageLessonType) !== String(selectedLessonType);

              if (lessonTypeMismatch) {
                this.f.form.controls['__field_students_inline_form']?.patchValue({ field_enrollment: null });
                // Do not block or show global errors for other lesson types; keep this minimal/silent.
              }

              // Check if package has field_lesson_type
              // Don't override if user has manually selected a lesson type
              if (packageData?.field_lesson_type && !this.lessonTypeManuallySelected) {
                LoggerService.log('Setting lesson type from package (enrollment selected):', packageData.field_lesson_type);

                // Set the lesson type in the form
                this.f.form.patchValue({
                  field_type: packageData.field_lesson_type
                });

                // Mark that lesson type has been set from package
                this.lessonTypeSetFromPackage = true;
              } else if (this.lessonTypeManuallySelected) {
                LoggerService.log('Skipping lesson type override - user has manually selected');
              }
            });
        }
      });
  }

  /**
   * Coaching mode: keep only enrollments of the chosen package, while the
   * lesson type is still Non Unit Private (1411).
   */
  protected override filterLessonEnrollments(results: any[], lessonType: string = null): any[] {
    if (!this.coachingPackage || String(lessonType || '') !== '1411') {
      this.coachingNoEnrollment = false;
      return results;
    }
    const filtered = (results || []).filter((enrollment: any) =>
      String(enrollment?.field_enrollment_package_name?.[0]?.id?.[0]?.value ?? '') === this.coachingPackage.id);
    this.coachingNoEnrollment = filtered.length === 0;
    return filtered;
  }

  /**
   * Coaching mode: students often hold several one-lesson enrollments of the
   * same package, so default to the oldest that still has a lesson left.
   * Falls back to the oldest when none does.
   */
  protected override pickDefaultLessonEnrollment(sortedResults: any[], lessonType: string = null): any {
    if (!this.coachingPackage || String(lessonType || '') !== '1411') {
      return super.pickDefaultLessonEnrollment(sortedResults, lessonType);
    }
    return sortedResults.find((enrollment: any) => Number(enrollment?.field_lesson_available?.[0]?.value) > 0)
      ?? sortedResults[0];
  }

  getEnrollmentById(id: string): any {
    return this.enrollmentsForUser?.find((e: any) => e?.id?.[0]?.value === id);
  }

  /**
   * When lesson type is Non Unit Private (1411), highlight zero remaining enrollments.
   */
  getNonUnitZeroClass(enrollment: any): string {
    const selectedLessonType = this.f?.value?.field_type;
    const isNonUnitPrivate = String(selectedLessonType || '') === '1411';
    const isNonUnitCategory = enrollment?.field_category?.[0]?.target_id == 66;
    const available = Number(enrollment?.field_lesson_available?.[0]?.value || 0);
    if (isNonUnitPrivate && isNonUnitCategory && available <= 0) {
      return 'text-primary';
    }
    return '';
  }

  override lookupStudentAccountContact(student_account: { option: { value: string; }; }, selectFirstContact = false, bundle: string = null) {
    LoggerService.log("lookupStudentAccountContact called...", student_account, selectFirstContact, bundle);

    // Fix for console logging undefined error.
    if (student_account == null) { return; }

    // Reset the lesson type flags when a new student is selected
    this.lessonTypeSetFromPackage = false;
    this.lessonTypeManuallySelected = false;

    let studentID: string;

    // Handle MatAutocompleteSelectedEvent
    if (student_account && typeof student_account === 'object' && student_account.option) {
      studentID = student_account.option.value;
    }
    // Handle direct string value
    else {
      studentID = student_account?.toString() || '';
    }

    studentID = this.regexStudentName(studentID);

    if (studentID == null || studentID == undefined) { return; }

    // Get student account data once and use it for both contact population and lesson type logic
    this._entityRESTService.getEntity('student_accounts', 'student_account', studentID)
      .subscribe(data => {
        LoggerService.log('Student account data:', data);

        // Handle contact population (parent logic)
        let contacts = "";
        let contactsID = [];
        let tmp = [];
        let selectOptions = [];

        // Support for couples (multiple contacts)
        if (Array.isArray(data['field_contacts'])) {
          // Filter out archived contacts (field_archived is "1" string when archived)
          const activeContacts = data['field_contacts'].filter(element => element.field_archived !== '1' && element.field_archived !== 1);

          activeContacts.forEach(element => {
            contactsID.push(element.id);
            tmp.push(element.title + ' (' + element.id + ')');
            selectOptions.push({ id: element.id, title: element.title });
          });

          contacts = tmp.join(', ');
          if (activeContacts.length > 1) {
            selectOptions.push({ id: contactsID.toString(), title: contacts });
          }
        } else if (data['field_contacts'] && data['field_contacts'].field_archived !== '1' && data['field_contacts'].field_archived !== 1) {
          // For a single contact (only if not archived)
          selectOptions.push({ id: data['field_contacts'].id, title: data['field_contacts'].title });
          contacts += data['field_contacts'].title + ' (' + data['field_contacts'].id + ')';
        }

        this.autoCompleteStudentContactOptions = selectOptions;
        this.f.form.controls['__field_students_inline_form']?.patchValue({
          field_students_contact: contacts
        });

        // For couples, select the combined option by default (only if selectFirstContact is true).
        // Defer the patch so the *ngFor radio buttons render first. A template-driven
        // mat-radio-group won't reflect a patched value if it's set before the radios exist.
        if (Array.isArray(data['field_contacts']) && selectFirstContact) {
          setTimeout(() => {
            this.f.form.controls['__field_students_inline_form']?.patchValue({
              field_students: contactsID.toString()
            });
          });
        }
        // For single contact, select the first contact
        else if (selectOptions.length > 0 && selectFirstContact) {
          const firstContactId = selectOptions[0].id;
          setTimeout(() => {
            this.f.form.controls['__field_students_inline_form']?.patchValue({
              field_students: firstContactId
            });
          });
        }

        // Additional lesson type logic - Load enrollments for this student to find the default/oldest one
        let endpoint = "/api_rest/v1/enrollmentLookupDetailed";
        let params = [
          { parameter: "u", value: studentID },
        ];

        this._drupalRESTService.httpGET(endpoint, params)
          .subscribe(enrollmentData => {
            LoggerService.log('Enrollment data for lesson type:', enrollmentData);
            let dataObj = Object.values(enrollmentData);

            LoggerService.log('All enrollments for student:', dataObj);

            // Filter for available enrollments (similar to the parent method)
            let availableEnrollments = dataObj.filter((object: any) => {
              let isPaymentPlusDropEqualTotalPrice = !(object.field_drop[0]?.value == object.field_balance_due[0]?.value);
              let isLessonAvailable = Number(object.field_lesson_available[0]?.value) > 0;
              let isSundryOrMisc = (object?.field_category?.[0]?.target_id == 67) || (object.field_category[0].target_id == 5);
              let isNonUnit = (object?.field_category?.[0]?.target_id == 66);

              LoggerService.log('Enrollment filter check:', {
                id: object.id[0].value,
                isPaymentPlusDropEqualTotalPrice,
                isLessonAvailable,
                isSundryOrMisc,
                isNonUnit,
                lessonAvailable: object.field_lesson_available[0]?.value,
                balanceDue: object.field_balance_due[0]?.value,
                drop: object.field_drop[0]?.value
              });

              // Exclude SUNDRY, MISC, and Non-Unit enrollments from auto-selection
              return isLessonAvailable && isPaymentPlusDropEqualTotalPrice && !isSundryOrMisc && !isNonUnit;
            });

            LoggerService.log('Available enrollments after filtering:', availableEnrollments);

            if (availableEnrollments.length > 0) {
              // Sort by sale date to get the oldest enrollment
              availableEnrollments.sort((a: any, b: any) => {
                const dateA = new Date(a.field_sale_date[0].value);
                const dateB = new Date(b.field_sale_date[0].value);
                return dateA.getTime() - dateB.getTime();
              });

              const oldestEnrollment = availableEnrollments[0];
              const enrollmentId = oldestEnrollment.id[0].value;

              LoggerService.log('Auto-setting lesson type from oldest enrollment:', enrollmentId);

              // Get the package data from the enrollment to set lesson type
              this._entityRESTService.getEntity('packages', 'enrollment', enrollmentId)
                .subscribe((enrollmentData: any) => {
                  LoggerService.log('Enrollment data for auto lesson type:', enrollmentData);

                  // Check if enrollment has a package reference
                  if (enrollmentData?.field_enrollment_package_name?.id) {
                    const packageId = enrollmentData.field_enrollment_package_name.id;
                    LoggerService.log('Looking up package ID for auto lesson type:', packageId, 'from enrollment:', enrollmentData);

                    // Get the package data to check for field_lesson_type
                    this._entityRESTService.getEntity('packages', 'package', packageId)
                      .subscribe((packageData: any) => {
                        LoggerService.log('Package data for auto lesson type:', packageData);
                        LoggerService.log('Package field_lesson_type value:', packageData?.field_lesson_type, 'type:', typeof packageData?.field_lesson_type);

                        // Check if package has field_lesson_type, not in edit mode, and hasn't been set yet
                        if (packageData?.field_lesson_type !== undefined && packageData?.field_lesson_type !== null && this.actionType !== 'edit' && !this.lessonTypeSetFromPackage && !this.coachingPackage) {
                          LoggerService.log('Auto-setting lesson type from package:', packageData.field_lesson_type);

                          // Set the lesson type in the form
                          this.f.form.patchValue({
                            field_type: packageData.field_lesson_type
                          });

                          // Also call updateLessonDefaultTimes to set the duration
                          this.updateLessonDefaultTimes(packageData.field_lesson_type);

                          // Mark that lesson type has been set from package
                          this.lessonTypeSetFromPackage = true;
                        }
                      });
                  }
                });
            } else {
              LoggerService.log('No available enrollments found, checking all enrollments for lesson type');

              // If no available enrollments, try using the first regular enrollment for lesson type
              if (dataObj.length > 0) {
                // Filter out Non-Unit enrollments from fallback selection too.
                // Also exclude system-generated packages (e.g. DE system) and fully-used
                // enrollments. A spent intro package should not drive lesson-type defaults.
                const regularEnrollments = dataObj.filter((object: any) => {
                  let isSundryOrMisc = (object?.field_category?.[0]?.target_id == 67) || (object.field_category[0].target_id == 5);
                  let isNonUnit = (object?.field_category?.[0]?.target_id == 66);
                  let isSystemPackage = object?.field_enrollment_package_name?.[0]?.field_is_system_package?.[0]?.value == '1';
                  let isFullyUsed = Number(object?.field_lesson_available?.[0]?.value ?? 0) <= 0;
                  return !isSundryOrMisc && !isNonUnit && !isSystemPackage && !isFullyUsed;
                });

                if (regularEnrollments.length > 0) {
                  // Sort regular enrollments by sale date to get the oldest
                  const sortedEnrollments = regularEnrollments.sort((a: any, b: any) => {
                    const dateA = new Date(a.field_sale_date[0].value);
                    const dateB = new Date(b.field_sale_date[0].value);
                    return dateA.getTime() - dateB.getTime();
                  });

                  const oldestEnrollment = sortedEnrollments[0];
                  const enrollmentId = oldestEnrollment.id[0].value;

                  LoggerService.log('Using oldest enrollment for lesson type (no available enrollments):', enrollmentId);

                  // Get the package data from the enrollment to set lesson type
                  this._entityRESTService.getEntity('packages', 'enrollment', enrollmentId)
                    .subscribe((enrollmentData: any) => {
                      LoggerService.log('Enrollment data for lesson type (fallback):', enrollmentData);

                      // Check if enrollment has a package reference
                      if (enrollmentData?.field_enrollment_package_name?.id) {
                        const packageId = enrollmentData.field_enrollment_package_name.id;
                        LoggerService.log('Looking up package ID for fallback lesson type:', packageId, 'from enrollment:', enrollmentData);

                        // Get the package data to check for field_lesson_type
                        this._entityRESTService.getEntity('packages', 'package', packageId)
                          .subscribe((packageData: any) => {
                            LoggerService.log('Package data for lesson type (fallback):', packageData);
                            LoggerService.log('Fallback package field_lesson_type value:', packageData?.field_lesson_type, 'type:', typeof packageData?.field_lesson_type);

                            // Check if package has field_lesson_type, not in edit mode, and hasn't been set yet
                            if (packageData?.field_lesson_type !== undefined && packageData?.field_lesson_type !== null && this.actionType !== 'edit' && !this.lessonTypeSetFromPackage && !this.coachingPackage) {
                              LoggerService.log('Setting lesson type from package (fallback):', packageData.field_lesson_type);

                              // Set the lesson type in the form
                              this.f.form.patchValue({
                                field_type: packageData.field_lesson_type
                              });

                              // Also call updateLessonDefaultTimes to set the duration
                              this.updateLessonDefaultTimes(packageData.field_lesson_type);

                              // Mark that lesson type has been set from package
                              this.lessonTypeSetFromPackage = true;
                            }
                          });
                      }
                    });
                }
              }
            }
          });
      });
  }

  updateDateTimeField(value: string) {
    this.isUpdatingFromFlatpickr = true;
    this.field_date_and_time = value;
    const parsedDate = this.initializeFlatpickrDisplayValue(value, true);

    if (!value) {
      this.flatpickrDisplayValue = '';
      this.flatpickrInstance?.clear();
      this.flatpickrDisplayValue = '';
      this.isUpdatingFromFlatpickr = false;
      return;
    }

    if (!this.useMobileSplitPicker && parsedDate && this.flatpickrInstance) {
      const currentDate = this.flatpickrInstance.selectedDates[0];
      if (!currentDate || currentDate.getTime() !== parsedDate.getTime()) {
        this.flatpickrInstance.setDate(parsedDate, false);
      }
    }

    if (this.useMobileSplitPicker && parsedDate) {
      this.syncMobilePickers(moment(parsedDate));
    }

    // Update landscape mode pickers if in landscape mode
    if (this.isLandscapeMode && parsedDate) {
      this.syncLandscapePickers(moment(parsedDate));
    }

    this.isUpdatingFromFlatpickr = false;
  }

  /**
   * Override _loadFieldsData to trigger lookups after form is patched with pre-filled data
   */
  override _loadFieldsData(fieldsData: any, formRef: any = null) {
    super._loadFieldsData(fieldsData, formRef);

    // After form is patched, check if student account was pre-filled and trigger lookups
    const prefilledStudentAccount = this.injectedDialogData?.['data']?.['fieldsData']?.['__field_students_inline_form']?.['field_student_account'];
    if (prefilledStudentAccount) {
      // Use setTimeout to ensure form patch completes first
      setTimeout(() => {
        const studentAccountEvent = { option: { value: prefilledStudentAccount } };
        this.lookupStudentAccountContact(studentAccountEvent, true);
        this.onStudentNameSelectThenLoadEnrollmentNameLESSONS(studentAccountEvent, false, null, true, 'createLesson', this.f?.value?.field_type);
      }, 100);
    }
  }

  // BreakpointObserver is app-lifetime; released in ngOnDestroy so closed dialogs can be collected.
  private breakpointSubscription: Subscription;

  override ngAfterViewInit(): void {
    // Set up breakpoint observer after view init to avoid change detection errors
    // Skip first emission since we already set initial value in constructor
    this.breakpointSubscription = this.breakpointObserver.observe([
      Breakpoints.HandsetLandscape,
      '(orientation: landscape) and (max-height: 600px)', // Custom query for mobile landscape
      '(max-width: 960px) and (orientation: landscape)'   // Tablets and phones in landscape
    ]).pipe(
      skip(1) // Skip the initial emission - we already set the value in constructor
    ).subscribe(result => {
      const wasLandscape = this._isLandscapeMode;
      this._isLandscapeMode = result.matches;

      LoggerService.log('[LANDSCAPE] Orientation changed. Was landscape:', wasLandscape, 'Is landscape:', result.matches);

      // If switching away from landscape mode, destroy the calendar
      if (wasLandscape && !result.matches && this.landscapeCalendarPicker) {
        LoggerService.log('[LANDSCAPE] Destroying calendar (switching to desktop)');
        this.landscapeCalendarPicker.destroy();
        this.landscapeCalendarPicker = undefined;
      }

      // If switching to landscape mode, initialize the calendar
      if (!wasLandscape && result.matches) {
        LoggerService.log('[LANDSCAPE] Initializing calendar (switching to landscape)');
        // Use longer timeout to ensure DOM is ready
        setTimeout(() => {
          const input = this.landscapeCalendarInput?.nativeElement;
          const wrapper = document.getElementById('landscape-calendar-wrapper');
          LoggerService.log('[LANDSCAPE] Input element:', input, 'Wrapper:', wrapper);

          if (input && wrapper) {
            this.initializeLandscapeCalendar(input);
          } else {
            LoggerService.warn('[LANDSCAPE] Input or wrapper not found when trying to initialize');
          }
        }, 150);
      }

      this._changeDetectorRef.detectChanges();
    });

    // Only initialize landscape calendar if we're actually in landscape mode
    // Wait for view to stabilize to check landscape mode properly
    setTimeout(() => {
      if (this.isLandscapeMode && this.landscapeCalendarInput?.nativeElement) {
        this.initializeLandscapeCalendar(this.landscapeCalendarInput.nativeElement);
      } else if (this.useMobileSplitPicker) {
        this.initializeMobileSplitPickers();
      } else if (this.flatpickrInput?.nativeElement) {
        this.initializeFlatpickr(this.flatpickrInput.nativeElement);
      }
    }, 0);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.breakpointSubscription?.unsubscribe();
    // flatpickr's document listeners keep the dialog alive until destroyed.
    this.flatpickrInstance?.destroy();
    this.flatpickrInstance = undefined;
    // Cleanup landscape calendar picker
    if (this.landscapeCalendarPicker) {
      this.landscapeCalendarPicker.destroy();
      this.landscapeCalendarPicker = undefined;
    }
  }

  initializeFlatpickr(input: HTMLInputElement): void {
    if (this.useMobileSplitPicker) {
      return;
    }

    const studioCountry = this.studioConfigCountry || 'US';
    const formats = this.localizationService.getDateFormatsForCountry(studioCountry);
    this.flatpickrDateFormat = `${formats.flatpickrDateFormat} h:i K`;

    const defaultDate = this.initializeFlatpickrDisplayValue(this.field_date_and_time, false);

    if (this.flatpickrInstance) {
      this.flatpickrInstance.set('minuteIncrement', 15);
      if (defaultDate) {
        this.flatpickrInstance.setDate(defaultDate, false);
      } else {
        this.flatpickrInstance.clear();
      }
      return;
    }

    this.flatpickrInstance = flatpickr(input, {
      dateFormat: this.flatpickrDateFormat,
      enableTime: true,
      disableMobile: true,
      minuteIncrement: 15,
      defaultDate,
      static: true,
      formatDate: (date) => this.formatDateForDisplay(date),
      parseDate: (value) => {
        const parsed = moment(value, this.localizationService.getDateFormatsForCountry(this.studioConfigCountry).shortDateTimeFormatMoment, true);
        if (parsed.isValid()) {
          return parsed.toDate();
        }
        return moment(value).isValid() ? moment(value).toDate() : undefined;
      },
      onChange: (selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
          const selectedDate = selectedDates[0];
          const formatted = moment(selectedDate).format('YYYY-MM-DDTHH:mm:ss');
          this.flatpickrDisplayValue = this.formatDateForDisplay(selectedDate);
          this.updateDateTimeField(formatted);
          this.checkScheduleConflicts();
          this.onDateChange(formatted);
        }
      },
      onOpen: (selectedDates, dateStr, instance) => {
        // Position the calendar using fixed positioning relative to the input
        const inputRect = input.getBoundingClientRect();
        const calendar = instance.calendarContainer;
        calendar.style.top = `${inputRect.bottom + 4}px`;
        calendar.style.left = `${inputRect.left}px`;
      },
      onClose: () => {},
    });
  }

  openFlatpickr(): void {
    if (this.useMobileSplitPicker) {
      return;
    }

    if (this.flatpickrInput?.nativeElement) {
      this.initializeFlatpickr(this.flatpickrInput.nativeElement);
    }

    if (this.flatpickrInstance) {
      this.flatpickrInstance.open();
    }
  }

  onFieldDateAndTimeModelChange(value: string) {
    if (this.isUpdatingFromFlatpickr) {
      return;
    }

    const parsedDate = this.initializeFlatpickrDisplayValue(value, true, true);

    if (!this.useMobileSplitPicker && this.flatpickrInstance) {
      if (parsedDate) {
        const currentDate = this.flatpickrInstance.selectedDates[0];
        if (!currentDate || currentDate.getTime() !== parsedDate.getTime()) {
          this.flatpickrInstance.setDate(parsedDate, false);
        }
      } else {
        this.flatpickrInstance.clear();
      }
    }

    if (this.useMobileSplitPicker) {
      if (parsedDate) {
        this.syncMobilePickers(moment(parsedDate));
      } else {
        this.syncMobilePickers(undefined);
      }
    }

    // Sync landscape mode pickers
    if (this.isLandscapeMode) {
      if (parsedDate) {
        this.syncLandscapePickers(moment(parsedDate));
      }
    }
  }

  private initializeFlatpickrDisplayValue(value?: string, keepRawOnInvalid: boolean = false, skipDisplayUpdate: boolean = false): Date | undefined {
    if (!value) {
      this.flatpickrDisplayValue = '';
      if (this.useMobileSplitPicker) {
        this.syncMobilePickers(undefined);
      }
      return undefined;
    }

    const parsed = moment(value, moment.ISO_8601, true);
    if (parsed.isValid()) {
      const dateObj = parsed.toDate();
      if (!skipDisplayUpdate) {
        this.flatpickrDisplayValue = this.formatDateForDisplay(dateObj);
      }
      if (this.useMobileSplitPicker) {
        this.syncMobilePickers(parsed);
      }
      return dateObj;
    }

    const fallback = moment(value);
    if (fallback.isValid()) {
      const fallbackDate = fallback.toDate();
      if (!skipDisplayUpdate) {
        this.flatpickrDisplayValue = this.formatDateForDisplay(fallbackDate);
      }
      if (this.useMobileSplitPicker) {
        this.syncMobilePickers(fallback);
      }
      return fallbackDate;
    }

    this.flatpickrDisplayValue = keepRawOnInvalid ? value : '';
    if (this.useMobileSplitPicker) {
      this.syncMobilePickers(undefined);
    }
    return undefined;
  }

  private formatDateForDisplay(date: Date): string {
    try {
      const formats: CountryDateFormats = this.localizationService.getDateFormatsForCountry(this.studioConfigCountry);
      const momentFormat = formats.shortDateTimeFormatMoment || 'MM/DD/YYYY h:mma';
      return moment(date).format(momentFormat);
    } catch (error) {
      LoggerService.warn('Failed to format date for display', error);
      return moment(date).format('YYYY-MM-DD HH:mm');
    }
  }

  private initializeMobileSplitPickers(): void {
    // Native HTML5 pickers: no JS init required, just seed the bound values.
    const defaultDate = this.field_date_and_time ? moment(this.field_date_and_time) : undefined;
    if (defaultDate?.isValid()) {
      this.syncMobilePickers(defaultDate);
    }
  }

  /** Called from the template when the user changes the native date input. */
  onMobileDateChange(value: string): void {
    this.mobileDateValue = value || '';
    this.commitMobileDateTime();
  }

  /** Called from the template when the user changes the native time input. */
  onMobileTimeChange(value: string): void {
    this.mobileTimeValue = value || '';
    this.commitMobileDateTime();
  }

  private commitMobileDateTime(): void {
    if (!this.mobileDateValue || !this.mobileTimeValue) {
      return;
    }

    // Parse strict to avoid silent fallbacks if the browser returns something odd.
    const combined = moment(`${this.mobileDateValue}T${this.mobileTimeValue}`, 'YYYY-MM-DDTHH:mm', true);
    if (!combined.isValid()) {
      return;
    }

    const formatted = combined.format('YYYY-MM-DDTHH:mm:ss');
    this.updateDateTimeField(formatted);
    this.flatpickrDisplayValue = this.formatDateForDisplay(combined.toDate());
    this.checkScheduleConflicts();
    this.onDateChange(formatted);
  }

  private syncMobilePickers(value?: moment.Moment): void {
    if (!value) {
      this.mobileDateValue = '';
      this.mobileTimeValue = '';
      return;
    }
    this.mobileDateValue = value.format('YYYY-MM-DD');
    this.mobileTimeValue = value.format('HH:mm');
  }

  private isMobileViewport(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    // (pointer: coarse) is true when the primary input is finger-based,
    // phones and tablets (including iPad Pro at 1366px wide). Desktops with
    // optional touchscreens still report (pointer: fine), so they keep the
    // desktop flatpickr overlay.
    return window.matchMedia('(pointer: coarse)').matches;
  }

  // Getter to expose landscape detection to the template
  // Only returns true for MOBILE devices in landscape orientation
  get isLandscapeMode(): boolean {
    return this._isLandscapeMode;
  }

  /**
   * Synchronously check if device is in landscape mode
   * Used for initial state to avoid change detection errors
   */
  private checkIsLandscapeMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const isMobile = window.matchMedia('(max-width: 768px)').matches ||
                     window.matchMedia('(max-width: 960px) and (orientation: landscape)').matches ||
                     window.matchMedia('(orientation: landscape) and (max-height: 600px)').matches;
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    return isMobile && isLandscape;
  }

  /**
   * Initialize calendar picker for landscape mode
   */
  initializeLandscapeCalendar(input: HTMLInputElement): void {
    // Guard: Don't initialize if not in landscape mode
    if (!this.isLandscapeMode) {
      LoggerService.log('[LANDSCAPE] Not in landscape mode, skipping calendar initialization');
      return;
    }

    const studioCountry = this.studioConfigCountry || 'US';
    const formats = this.localizationService.getDateFormatsForCountry(studioCountry);

    const defaultDate = this.field_date_and_time ? moment(this.field_date_and_time).toDate() : undefined;

    // Initialize time picker values from existing date
    if (defaultDate) {
      this.syncLandscapePickers(moment(defaultDate));
    } else {
      // Scroll to default hour (12 AM) on initial load
      setTimeout(() => {
        this.scrollToActiveButton('hour-period');
        this.scrollToActiveButton('minute');
      }, 200);
    }

    // Find the wrapper element to append the calendar to
    const wrapper = document.getElementById('landscape-calendar-wrapper');

    if (!wrapper) {
      LoggerService.log('[LANDSCAPE] Wrapper not found, cannot initialize calendar');
      return;
    }

    this.landscapeCalendarPicker = flatpickr(input, {
      dateFormat: formats.flatpickrDateFormat,
      defaultDate,
      disableMobile: true,
      inline: true, // Always show calendar
      appendTo: wrapper, // Append to wrapper to keep it contained
      onChange: (selectedDates) => {
        if (selectedDates?.length) {
          this.updateLandscapeDateTime(moment(selectedDates[0]));
        }
      },
    });

    LoggerService.log('[LANDSCAPE] Calendar initialized successfully');
  }

  /**
   * Sync landscape picker values from a moment date
   */
  private syncLandscapePickers(date: moment.Moment): void {
    if (!date || !date.isValid()) {
      return;
    }

    this.landscapeHour = parseInt(date.format('h'));
    this.landscapeMinute = date.format('mm');
    this.landscapePeriod = date.format('A');

    // Update the calendar picker if it exists
    if (this.landscapeCalendarPicker) {
      const currentDate = this.landscapeCalendarPicker.selectedDates[0];
      if (!currentDate || currentDate.getTime() !== date.toDate().getTime()) {
        this.landscapeCalendarPicker.setDate(date.toDate(), false);
      }
    }

    // Scroll to show the selected values (combined hour-period and minute)
    this._changeDetectorRef.detectChanges();
    this.scrollToActiveButton('hour-period');
    this.scrollToActiveButton('minute');
  }

  /**
   * Update the combined date/time when landscape mode changes
   */
  updateLandscapeDateTime(date?: moment.Moment): void {
    const selectedDate = date || (this.landscapeCalendarPicker?.selectedDates[0] ? moment(this.landscapeCalendarPicker.selectedDates[0]) : null);

    if (!selectedDate) {
      return;
    }

    // Convert 12-hour to 24-hour format
    let hour24 = parseInt(this.landscapeHour.toString());
    if (this.landscapePeriod === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (this.landscapePeriod === 'AM' && hour24 === 12) {
      hour24 = 0;
    }

    const combined = moment(selectedDate).set({
      hour: hour24,
      minute: parseInt(this.landscapeMinute),
      second: 0
    });

    if (!combined.isValid()) {
      return;
    }

    const formatted = combined.format('YYYY-MM-DDTHH:mm:ss');
    this.updateDateTimeField(formatted);
    this.checkScheduleConflicts();
    this.onDateChange(formatted);
  }

  /**
   * Handle combined hour + period change
   */
  onLandscapeHourPeriodChange(hour: number, period: string): void {
    this.landscapeHour = hour;
    this.landscapePeriod = period;
    this.updateLandscapeDateTime();
    this.scrollToActiveButton('hour-period');
  }

  /**
   * Handle landscape minute change
   */
  onLandscapeMinuteChange(minute: string): void {
    this.landscapeMinute = minute;
    this.updateLandscapeDateTime();
    this.scrollToActiveButton('minute');
  }

  /**
   * Scroll to center the active button in the time picker
   */
  private scrollToActiveButton(type: 'hour-period' | 'minute'): void {
    setTimeout(() => {
      const scrollContainer = document.querySelector(`.time-buttons-scroll.${type}-scroll`) as HTMLElement;
      const activeButton = scrollContainer?.querySelector('.time-btn.active') as HTMLElement;

      if (scrollContainer && activeButton) {
        // Get the button's position relative to the scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // Calculate the current scroll position
        const currentScroll = scrollContainer.scrollLeft;

        // Calculate where the button is relative to the container's scroll position
        const buttonRelativeLeft = buttonRect.left - containerRect.left + currentScroll;

        // Calculate scroll position to center the button
        // Center = button's left position + (button width / 2) - (container width / 2)
        const scrollPosition = buttonRelativeLeft + (buttonRect.width / 2) - (containerRect.width / 2);

        scrollContainer.scrollTo({
          left: Math.max(0, scrollPosition), // Don't scroll negative
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  override calcDateEOM(): string {
    // Calculate end of current month and return the date string
    const date = moment().endOf('month').format('YYYY-MM-DD');
    this.field_expiration_date = date;
    return date;
  }

  override calcDateEOY(): string {
    // Calculate end of current year and return the date string
    const date = moment().endOf('year').format('YYYY-MM-DD');
    this.field_expiration_date = date;
    return date;
  }

  onSpecificDateFieldClick(event: any) {
    // When user clicks the date field, select the "specific date" radio button
    event.stopPropagation();
    this.field_expiration_date = this.valueSpecificDate;
  }

  onSpecificDateSelected(dateValue: any) {
    // When a date is selected, keep the radio button selected and store in field_expiration_date
    if (dateValue) {
      this.field_expiration_date = this.valueSpecificDate;
      this.specificExpirationDateValue = dateValue;
    }
  }

  // --- Recurring series methods ---

  onRecurrenceChanged(config: RecurrenceConfig | null): void {
    this.isRecurring = !!config;
    this.recurrenceConfig = config;
    this.seriesConflicts = [];
  }

  override onSubmit(form: any): void {
    this.commitPendingAttendee();
    if (this.isRecurring && this.recurrenceConfig && this.actionType === 'create') {
      this.saveAsRecurring(form);
      return;
    }
    super.onSubmit(form);
  }

  /** Assemble the bundle-specific save options for StandingSeriesSaveService. */
  private buildSeriesSaveOptions(form: any) {
    const startDateObj = this.field_date_and_time ? new Date(this.field_date_and_time) : new Date();
    const startDate = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;
    const attendee = this.field_student?.[0] || {};
    return {
      bundle: 'lesson' as const,
      noun: 'appointments',
      operatorId: this.field_instructor || '',
      includeEnrollment: true,
      config: this.recurrenceConfig!,
      attendee,
      studentId: this.extractIdFromAutocomplete(attendee.field_student_account || ''),
      studioId: this._authService.selectedStudio?.id,
      time: this.field_date_and_time ? new Date(this.field_date_and_time).toTimeString().substring(0, 5) : '12:00',
      startDate,
      duration: form?.value?.field_duration || '00:45',
      typeId: form?.value?.field_type || '',
      notes: form?.value?.field_notes || '',
    };
  }

  /** React to a save/generate result with the appropriate UI. */
  private applyRecurringResult(result: StandingSeriesSaveResult): void {
    if (result.kind === 'conflicts') {
      this.pendingSeriesId = result.seriesId;
      this.seriesConflicts = result.conflicts;
      this.isSaving = false;
    } else if (result.kind === 'created') {
      const msg = result.skipped > 0
        ? `Created ${result.count} recurring appointments (${result.skipped} skipped).`
        : `Created ${result.count} recurring appointments.`;
      this._snackBar.open(msg, 'OK', { duration: 5000 });
      this.closeDialog(result.data);
    } else {
      this.isSaving = false;
      this.errorMessage = result.message;
    }
  }

  private saveAsRecurring(form: any): void {
    if (this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];
    // A re-save while conflicts are showing starts a fresh series, so retire
    // the one still pending from the previous attempt.
    if (this.pendingSeriesId) {
      this._seriesSave.cancel(this.pendingSeriesId).subscribe(() => {}, () => {});
      this.pendingSeriesId = null;
    }
    this._seriesSave.createAndGenerate(this.buildSeriesSaveOptions(form))
      .subscribe(result => this.applyRecurringResult(result));
  }

  generateIgnoringConflicts(): void {
    if (!this.pendingSeriesId || this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];
    this._seriesSave.generateIgnoringConflicts(this.pendingSeriesId, this.buildSeriesSaveOptions(this.f))
      .subscribe(result => this.applyRecurringResult(result));
  }

  cancelRecurringSave(): void {
    if (this.pendingSeriesId) {
      this._seriesSave.cancel(this.pendingSeriesId).subscribe(() => {}, () => {});
    }
    this.seriesConflicts = [];
    this.pendingSeriesId = null;
    this.isSaving = false;
  }

  /**
   * Template helper for the <app-standing-series> studentAccountId binding.
   * Self-contained (not delegated to _seriesSave) because this runs during
   * change detection, independent of when the service field is built.
   */
  extractIdFromAutocomplete(value: string): string {
    if (!value) return '';
    if (!isNaN(+value)) return value;
    const match = /\(([^)]+)\)(?!.*\()/.exec(value);
    return match ? match[1] : value;
  }


  override __alterValues(values: any) {
    // If user selected "specific date" option, use the specificExpirationDateValue
    if (values.field_expiration_date === this.valueSpecificDate) {
      if (this.specificExpirationDateValue) {
        values.field_expiration_date = this.specificExpirationDateValue;
      } else {
        // No specific date was selected, clear the field
        values.field_expiration_date = null;
      }
    }

    // Call parent's __alterValues to handle the rest
    super.__alterValues(values);
  }

  override onDeleteSubmit(form: any) {
    let id = this._injectedDialogData['data']['EntityID'];

    LoggerService.log('delete lesson called...', id);

    this.displayProgressSpinner(true);
    this._entityRESTService.deleteEntity(this.eckType, this.bundle, id)
      .subscribe(data => {
        // Only close the current dialog, not all dialogs
        this.closeDialog();

        this.displayProgressSpinner(false);
      },
        error => this.handleError(error)
      );
  }
}

